namespace $ {

	export type $shm_tests_app_artifact = {
		kind: string
		path: string
		mime: string
		size: number
		sha256: string
	}

	export type $shm_tests_app_step = {
		title: string
		status: string
		started_offset_ms: number
		duration_ms: number
		covers?: readonly string[]
		artifacts?: readonly string[]
		video?: string
		benchmarks?: readonly $shm_tests_app_benchmark[]
		visual?: readonly $shm_tests_visual_comparison[]
	}

	export type $shm_tests_visual_comparison = {
		key: string
		profile: string
		status: 'unchanged' | 'changed' | 'size_changed' | 'missing_baseline' | 'updated' | 'error'
		baseline: string
		actual: string
		expected: string | null
		diff: string | null
		changed_pixels?: number
		changed_ratio?: number
		dimensions?: {
			expected: { width: number, height: number }
			actual: { width: number, height: number }
		}
		bounds?: { left: number, top: number, right: number, bottom: number } | null
		threshold?: number
		max_diff_ratio?: number
		actual_sha256?: string
		baseline_sha256?: string
	}

	export type $shm_tests_app_benchmark = {
		title: string
		value: number
		unit: string
		better?: 'lower' | 'higher'
		note?: string
	}

	export type $shm_tests_app_result = {
		schema_version: number
		scenario: {
			id: string
			title: string
			kind: string
			source: string
			group?: string
			covers?: readonly string[]
		}
		run: {
			id: string
			status: string
			started_at: string
			finished_at: string
			duration_ms: number
		}
		source: {
			repository: string
			commit: string
			branch: string
			dirty: boolean
			dirty_diff_sha256: string
		}
		targets: readonly {
			role: string
			app_version: string
			build_date: string
		}[]
		runner: {
			script: string
			node_version: string
			platform: string
			arch: string
		}
		media?: {
			video_started_offset_ms: null | number
			video_stopped_offset_ms?: null | number
			video_preroll_ms?: number
			video_postroll_ms?: number
			video_mode?: 'none' | 'full' | 'segments'
			segmentation_error?: string
		}
		visual?: {
			status: string
			total: number
			counts: Record< string, number >
		}
		steps: readonly $shm_tests_app_step[]
		artifacts: readonly $shm_tests_app_artifact[]
		error: null | {
			message: string
			stack?: string
		}
	}

	export type $shm_tests_app_index_entry = {
		id: string
		manifest: string
		status: string
		started_at: string
		duration_ms: number
		scenario: $shm_tests_app_result[ 'scenario' ]
		source: Pick< $shm_tests_app_result[ 'source' ], 'commit' | 'dirty' >
		targets: $shm_tests_app_result[ 'targets' ]
	}

	export type $shm_tests_app_index = {
		schema_version: number
		updated_at: null | string
		runs: readonly $shm_tests_app_index_entry[]
	}

}

namespace $.$$ {

	export class $shm_tests_app extends $.$shm_tests_app {

		index_uri() {
			return '/shm/tests/results/index.json'
		}

		@ $mol_mem
		index() {
			return this.$.$mol_fetch.json( this.index_uri(), { cache: 'no-store' } ) as $shm_tests_app_index
		}

		entries() {
			return this.index().runs
		}

		override spread_ids() {
			return [ ... new Set( this.entries().map( entry => entry.scenario.id ) ) ]
		}

		override Spread( id: string ) {
			if( id === 'releases' ) return this.Releases()
			return this.Scenario( id )
		}

		scenario_pages( id: string ) {
			return [
				this.Scenario_panel( id ),
				this.screenshots_open( id )
					? this.Scenario_screenshots( id )
					: this.benchmarks_open( id )
						? this.Scenario_benchmarks( id )
						: this.Scenario_step( id ),
			]
		}

		@ $mol_mem_key
		override benchmarks_open( id: string, next?: boolean ) {
			if( next !== undefined ) this.$.$mol_state_arg.value( 'test_benchmarks', next ? 'yes' : null )
			return this.$.$mol_state_arg.value( 'test_benchmarks' ) === 'yes'
		}

		@ $mol_mem_key
		override screenshots_open( id: string, next?: boolean ) {
			if( next !== undefined ) this.$.$mol_state_arg.value( 'test_screenshots', next ? 'yes' : null )
			return this.$.$mol_state_arg.value( 'test_screenshots' ) === 'yes'
		}

		override Spread_default() {
			const id = this.default_scenario()
			return id ? this.Scenario( id ) : this.Empty()
		}

		override arg( id: string ) {
			return super.arg( id === this.default_scenario() ? '' : id )
		}

		default_scenario() {
			const run = this.$.$mol_state_arg.value( 'test_run' ) ?? ''
			return this.entry( run )?.scenario.id ?? this.spread_ids()[ 0 ] ?? ''
		}

		override spread_title( id: string ) {
			if( id === 'releases' ) return 'Проекты и релизы'
			const entry = this.scenario_entries( id )[ 0 ]
			if( !entry ) return id
			return `${ this.status_icon( entry.status ) } ${ this.scenario_title( id ) }`
		}

		scenario_title( id: string ) {
			switch( id ) {
				case 'ba-ba-admin-large-journey-video':
				case 'ba-ba-mentionstat-mock-video': return 'Общий сценарий'
			}
			return this.scenario_entries( id )[ 0 ]?.scenario.title ?? id
		}

		group_ids() {
			return [ 'maps', 'entities', 'mentionstat', 'mention_stats', 'wordstat', 'pocketbase' ]
		}

		group_title( id: string ) {
				switch( id ) {
				case 'maps': return 'Hitalama карты'
				case 'entities': return 'Таблицы и сущности'
				case 'mentionstat': return 'Мониторинг упоминаний'
				case 'mention_stats': return 'Статистика упоминаний'
				case 'wordstat': return 'Wordstat'
				case 'pocketbase': return 'PocketBase админка'
				default: return 'Другие'
			}
		}

		scenario_group( id: string ) {
			const entry = this.scenario_entries( id )[ 0 ]
			if( !entry ) return 'other'
			const haystack = [
				entry.scenario.id,
				entry.scenario.source,
				...( entry.scenario.covers ?? [] ),
			].join( ' ' ).toLowerCase()
				.replaceAll( '$' + 'hitalama_ba_', '$shm_ba_' )
				.replaceAll( '$shm_hitalama_ba_', '$shm_ba_' )
				.replaceAll( '$' + 'hitalama_', '$shm_hitalama_' )
			if( /(?:^|[-_/.$])table2(?:[-_/.$]|$)|map-entity-schema|widget_(?:entity_table2|float_(?:table2|form2|entity_editor))/.test( haystack ) ) return 'entities'
			if([ ... this.group_ids(), 'other' ].includes( entry.scenario.group ?? '' )) return entry.scenario.group!
			if( /(?:ba|dashboard)(?:[-_/].*)?admin|ba-ba-admin|\$shm_(?:ba|dashboard)_admin|pocketbase/.test( haystack ) ) return 'pocketbase'
			if( /ba(?:[-_/].*)?mention[_-]?stats|ba-ba-mentionstat|\$shm_ba_mention_stats/.test( haystack ) ) return 'mention_stats'
			if( /wordstat/.test( haystack ) ) return 'wordstat'
			if( /mention[_-]?stat/.test( haystack ) ) return 'mentionstat'
			if( /(?:^|[-_/.$])map(?:[-_/.$]|$)|hitalama-scenario/.test( haystack ) ) return 'maps'
			return 'other'
		}

		group_scenario_ids( group: string ) {
			return this.spread_ids_filtered().filter( id => this.scenario_group( id ) === group )
		}

		group_expandable( id: string ) {
			return this.group_scenario_ids( id ).length > 0
		}

		group_rows( id: string ) {
			return [
				this.Group_row( id ),
				... this.group_expanded( id ) && this.group_expandable( id ) ? [ this.Group_children( id ) ] : [],
			]
		}

		group_scenario_rows( id: string ) {
			return this.group_scenario_ids( id ).map( scenario => this.Scenario_node( scenario ) )
		}

		scenario_node_content( id: string ) {
			return [
				this.Scenario_guide_vertical( id ),
				this.Scenario_guide_horizontal( id ),
				this.Menu_item( id ),
			]
		}

		scenario_guide_bottom( id: string ) {
			const scenarios = this.group_scenario_ids( this.scenario_group( id ) )
			return scenarios.at( -1 ) === id ? '50%' : '0px'
		}

		override menu_links() {
			const scenarios = this.spread_ids_filtered()
			const groups = [ ... this.group_ids() ]
			if( scenarios.some( id => this.scenario_group( id ) === 'other' ) ) groups.push( 'other' )
			return [ this.Releases_link(), ... groups.map( group => this.Group( group ) ) ]
		}

		status_icon( status: string ) {
			switch( status ) {
				case 'passed': return '✓'
				case 'failed': return '✕'
				case 'aborted': return '■'
				default: return '○'
			}
		}

		duration( milliseconds: number ) {
			if( milliseconds < 1000 ) return `${ milliseconds } мс`
			return `${ ( milliseconds / 1000 ).toFixed( 1 ) } с`
		}

		@ $mol_mem_key
		entry( id: string ) {
			return this.entries().find( entry => entry.id === id ) ?? null
		}

		@ $mol_mem_key
		scenario_entries( id: string ) {
			return this.entries().filter( entry => entry.scenario.id === id )
		}

		@ $mol_mem_key
		run_versions( scenario: string ) {
			return Object.fromEntries( this.scenario_entries( scenario ).map( entry => {
				const target = entry.targets.find( target => target.role === 'browser_app' ) ?? entry.targets[ 0 ]
				const date = new Date( entry.started_at ).toLocaleString( 'ru-RU' )
				const commit = entry.source.commit ? entry.source.commit.slice( 0, 8 ) : 'unknown'
				return [ entry.id, `${ this.status_icon( entry.status ) } ${ date } · ${ target?.app_version || 'unknown' } · ${ commit }` ]
			} ) )
		}

		@ $mol_mem_key
		override selected_run( scenario: string, next?: string ) {
			const entries = this.scenario_entries( scenario )
			const latest = entries[ 0 ]?.id ?? ''
			if( next !== undefined ) {
				const selected = entries.some( entry => entry.id === next ) ? next : latest
				this.$.$mol_state_arg.value( 'test_run', selected === latest ? null : selected )
				return selected
			}
			const selected = this.$.$mol_state_arg.value( 'test_run' ) ?? ''
			return entries.some( entry => entry.id === selected ) ? selected : latest
		}

		@ $mol_mem_key
		selected_result( scenario: string ) {
			return this.result( this.selected_run( scenario ) )
		}

		@ $mol_mem_key
		comparison_results( scenario: string ) {
			return this.scenario_entries( scenario )
				.slice( 0, 3 )
				.map( entry => this.result( entry.id ) )
				.filter( result => result !== null )
		}

		@ $mol_mem_key
		selected_manifest_uri( scenario: string ) {
			return this.result_manifest_uri( this.selected_run( scenario ) )
		}

		@ $mol_mem_key
		result_manifest_uri( id: string ) {
			const manifest = this.entry( id )?.manifest ?? ''
			return manifest ? `/shm/tests/${ manifest.replace( /^\/+/, '' ) }` : ''
		}

		@ $mol_mem_key
		result( id: string ) {
			const uri = this.result_manifest_uri( id )
			if( !uri ) return null
			return this.$.$mol_fetch.json( uri, { cache: 'no-store' } ) as $shm_tests_app_result
		}

		override scenario_player( id: string ) {
			return this.Scenario_step( id ).Player()
		}

	}

}
